<template>
  <default>
    <h1 class="text-center">Přihlášení do aplikace</h1>
    <section class="bg-white dark:bg-gray-900">
      <div v-if="appStore.isAuthenticated && appStore.user" class="text-center mx-auto">
        <p class="h4 mb-1">{{ userDisplayName }}</p>
        <p class="text-muted mb-3">{{ appStore.user.email }}</p>

        <div v-if="hasCompletedRegistration" class="alert alert-success col-md-8 mx-auto">
          Účet je připraven, přesměrování do objednávkového formuláře...
        </div>

        <div v-else-if="appStore.projects" class="col-md-8 mx-auto text-start">
          <div v-if="appStore.projects.length === 0 || appStore.selectedProject" class="card">
            <div class="card-body">
              <h2 class="h5 mb-3">Aktivace účtu (samoregistrace)</h2>
              <p class="text-muted mb-3">
                Zadejte IČO a vyberte provozovnu. Po potvrzení se váš účet automaticky aktivuje.
              </p>

              <div class="mb-3">
                <label for="ico-input" class="form-label">IČO</label>
                <div class="input-group">
                  <input
                      id="ico-input"
                      v-model.trim="ico"
                      type="text"
                      inputmode="numeric"
                      class="form-control"
                      maxlength="8"
                      placeholder="Např. 12345678"
                      @input="onIcoInput"
                      @keyup.enter="startOnboarding"
                  />
                  <button
                      class="btn btn-primary text-white"
                      :disabled="loading || ico.length === 0"
                      @click="startOnboarding"
                  >
                    <span v-if="loading && !addresses.length" class="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
                    Najít provozovny
                  </button>
                </div>
              </div>

              <div v-if="addresses.length > 0" class="mb-3">
                <address-select
                    :model-value="selectedAddressId"
                    :external-addresses="addresses"
                    :use-external-addresses="true"
                    :external-loading="loading"
                    confirm-button-text="Potvrdit a aktivovat účet"
                    @update:modelValue="selectedAddressId = $event"
                    @confirm="confirmAddressSelection"
                />
                <button class="btn btn-outline-secondary" :disabled="loading" @click="clearAddressSelection">
                  Změnit IČO
                </button>
              </div>

              <div v-if="onboardingInfo" class="alert alert-info mb-2">{{ onboardingInfo }}</div>
              <div v-if="onboardingError" class="alert alert-danger mb-2">{{ onboardingError }}</div>
              <div v-if="onboardingSuccess" class="alert alert-success mb-2">{{ onboardingSuccess }}</div>

              <p class="mb-0 mt-3">
                Pokud se registrace nedaří, kontaktujte nás na
                <a href="mailto:webmaster@girafruit.cz">webmaster@girafruit.cz</a>
                nebo na tel. <a href="tel:+420724034499">724 034 499</a>.
              </p>
            </div>
          </div>

          <div v-else class="alert alert-warning">
            Uživatel má přístup do jiného projektu, ale ne do cílového Gira projektu.
            Zkontrolujte nastavení proměnné VITE_PROJECT_ID (případně VITE_GIRA_PROJECT_ID) nebo kontaktujte správce.
          </div>
        </div>

        <div v-else class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <div v-else class="text-center">
        <button
            @click="appStore.login"
            class="btn btn-primary text-white"
        >
          Přihlásit se
        </button>
      </div>
    </section>
  </default>
</template>

<script>
import {useAppStore} from "@/stores/appStore";
import Default from "@/layouts/default.vue";
import AddressSelect from "@/components/gira/addressSelect.vue";

export default {
  name: "AuthView",
  components: {AddressSelect, Default},
  data() {
    return {
      appStore: useAppStore(),
      ico: "",
      addresses: [],
      selectedAddressId: "",
      onboardingInfo: "",
      onboardingError: "",
      onboardingSuccess: "",
      loading: false,
    };
  },
  computed: {
    userDisplayName() {
      return this.appStore.user?.name || this.appStore.user?.email || "Přihlášený uživatel";
    },
    hasCompletedRegistration() {
      const customer = this.appStore.customer;
      return Boolean(
          this.appStore.selectedProject &&
          customer?.addresses?.shipping &&
          customer?.addresses?.billing
      );
    },
  },
  watch: {
    hasCompletedRegistration: {
      immediate: true,
      handler(isCompleted) {
        if (isCompleted) {
          this.redirectToOrder();
        }
      },
    },
  },
  methods: {
    async redirectToOrder() {
      if (this.$route?.name !== "auth") {
        return;
      }

      await this.$router.push({name: "order"}).catch(() => null);
    },
    onIcoInput(event) {
      const value = event.target.value.replace(/\D/g, "").slice(0, 8);
      this.ico = value;
    },
    clearAddressSelection() {
      this.addresses = [];
      this.selectedAddressId = "";
      this.onboardingInfo = "";
      this.onboardingError = "";
      this.onboardingSuccess = "";
    },
    resolveConfiguredProjectId() {
      const configuredProjectId = import.meta.env.VITE_PROJECT_ID || import.meta.env.VITE_GIRA_PROJECT_ID;
      if (typeof configuredProjectId !== "string") {
        return undefined;
      }
      const normalized = configuredProjectId.trim();
      return normalized.length > 0 ? normalized : undefined;
    },
    async reloadAppContext(onboardingPayload) {
      this.appStore.mongoUser = await this.$usersApi.getMe().then(response => response.data);
      this.appStore.projects = await this.$usersApi.getUsersProjects().then(response => response.data);

      const preferredProjectId = onboardingPayload?.projectId || this.resolveConfiguredProjectId();
      this.appStore.selectedProject = this.appStore.projects.find(project => project._id === preferredProjectId) || null;
      if (!this.appStore.selectedProject && this.appStore.projects.length === 1) {
        this.appStore.selectedProject = this.appStore.projects[0];
      }

      if (!this.appStore.selectedProject) {
        this.onboardingError = "Aktivace proběhla, ale cílový projekt nebyl v účtu nalezen.";
        return;
      }

      if (onboardingPayload?.customer) {
        this.appStore.customer = onboardingPayload.customer;
      }

      this.appStore.customer = await this.$customersApi.findCustomerByEmail(
          this.appStore.selectedProject.spaceId,
          this.appStore.selectedProject._id,
          this.appStore.mongoUser.email
      ).then(response => response.data).catch(() => null);

      if (!this.appStore.customer) {
        this.appStore.customer = await this.$customersApi.createCustomer(
            this.appStore.selectedProject.spaceId,
            this.appStore.selectedProject._id,
            {
              name: this.appStore.mongoUser.email,
              email: this.appStore.mongoUser.email,
            }
        ).then(response => response.data).catch(() => null);
      }
    },
    async processOnboardingPayload(payload, fromAddressSelection = false) {
      if (!payload) {
        this.onboardingError = "Onboarding endpoint nevrátil očekávaná data.";
        return;
      }

      if (payload.onboardingCompleted) {
        await this.reloadAppContext(payload);
        if (!this.appStore.selectedProject) {
          return;
        }

        this.onboardingInfo = "";
        this.onboardingError = "";
        this.onboardingSuccess = payload.alreadyOnboarded
            ? "Účet už byl aktivovaný, pokračujeme do objednávkového formuláře."
            : "Účet byl úspěšně aktivovaný.";
        await this.$router.push({name: "order"});
        return;
      }

      if (payload.onboardingEnabled === false) {
        this.onboardingError = "Samoregistrace není na API aktuálně zapnutá.";
        return;
      }

      if (payload.requiresAddressSelection && Array.isArray(payload.addresses) && payload.addresses.length > 0) {
        this.addresses = payload.addresses;
        this.selectedAddressId = payload.selectedAddressId || "";
        this.onboardingSuccess = "";
        this.onboardingError = "";
        this.onboardingInfo = "Pro pokračování vyberte provozovnu a potvrďte aktivaci.";
        return;
      }

      this.onboardingError = fromAddressSelection
          ? "Aktivaci se nepodařilo dokončit. Zkuste to prosím znovu."
          : "Pro zadané IČO nebyly nalezeny žádné provozovny.";
    },
    async handleOnboardingError(error, fromAddressSelection = false) {
      const payload = error?.response?.data;
      if (payload && typeof payload === "object") {
        await this.processOnboardingPayload(payload, fromAddressSelection);
        if (this.onboardingError || this.onboardingInfo || this.onboardingSuccess || this.addresses.length > 0) {
          return;
        }
      }

      if (error?.response?.status === 404) {
        this.onboardingError = "Pro zadané IČO nebyly nalezeny žádné provozovny nebo onboarding není aktivní.";
        return;
      }

      this.onboardingError = "Nepodařilo se komunikovat s API. Zkuste to prosím znovu.";
    },
    async startOnboarding() {
      if (!this.ico || this.loading) {
        return;
      }

      this.loading = true;
      this.addresses = [];
      this.selectedAddressId = "";
      this.onboardingInfo = "";
      this.onboardingError = "";
      this.onboardingSuccess = "";

      try {
        const response = await this.$modulesGiraApi.onboard({ico: this.ico});
        await this.processOnboardingPayload(response.data);
      } catch (error) {
        await this.handleOnboardingError(error);
      } finally {
        this.loading = false;
      }
    },
    async confirmAddressSelection(selectedAddressIdFromComponent) {
      const effectiveAddressId = selectedAddressIdFromComponent || this.selectedAddressId;
      if (!this.ico || !effectiveAddressId || this.loading) {
        return;
      }

      this.selectedAddressId = effectiveAddressId;
      this.loading = true;
      this.onboardingError = "";
      this.onboardingSuccess = "";
      this.onboardingInfo = "";

      try {
        const response = await this.$modulesGiraApi.onboard({
          ico: this.ico,
          addressId: effectiveAddressId,
        });
        await this.processOnboardingPayload(response.data, true);
      } catch (error) {
        await this.handleOnboardingError(error, true);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
